import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControlLabel,
    Checkbox,
    TextField,
    Box,
    Typography,
    Grid,
    Alert,
    CircularProgress,
    Divider,
    IconButton,
    Tooltip,
    LinearProgress
} from '@mui/material';
import { 
    Info as InfoIcon,
    Upload as UploadIcon,
    ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { Project } from '../../types/project';
import { projectService } from '../../services/projectService';
import type { UploadResponse } from '../../services/projectService';

interface ShareProposalDialogProps {
    open: boolean;
    onClose: () => void;
    project: Project;
}

interface Section {
    id: string;
    title: string;
    required_fields: string[];
}

// Rótulos em português dos campos técnicos devolvidos pelo backend (antes aparecia "Description", "team_members"...)
const FIELD_LABELS: Record<string, string> = {
    description: 'Descrição',
    milestones: 'Marcos',
    team_members: 'Membros da equipe',
    deliverables: 'Entregas',
    conditions: 'Condições',
    payment_terms: 'Condições de pagamento',
};
const fieldLabel = (field: string) =>
    FIELD_LABELS[field] || field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');

interface SectionContent {
    [key: string]: {
        [key: string]: string | string[] | string[][];
    };
}

interface SelectedSections {
    [key: string]: boolean;
}

const ShareProposalDialog: React.FC<ShareProposalDialogProps> = ({
    open,
    onClose,
    project
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [sections, setSections] = useState<Section[]>([]);
    const [selectedSections, setSelectedSections] = useState<SelectedSections>({});
    const [sectionContent, setSectionContent] = useState<SectionContent>({});
    const [shareUrl, setShareUrl] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});

    useEffect(() => {
        if (open) {
            loadAvailableSections();
        } else {
            // Reset states when dialog closes
            setShareUrl(null);
            setError(null);
            setSectionContent({});
            setSelectedSections({});
            setUploadProgress({});
        }
    }, [open]);

    const loadAvailableSections = async () => {
        try {
            setLoading(true);
            const response = await projectService.getAvailableSections();
            // Filter out the Qaracter section from the selection list
            const filteredSections = response.filter(section => section.id !== 'qaracter');
            setSections(filteredSections);
            
            // Initialize states with available sections
            const initialSelectedSections: SelectedSections = {};
            const initialSectionContent: SectionContent = {};
            
            // Always include Qaracter section
            initialSelectedSections['qaracter'] = true;
            
            // Initialize content for Qaracter section
            const qaracterSection = response.find(s => s.id === 'qaracter');
            if (qaracterSection) {
                initialSectionContent['qaracter'] = qaracterSection.required_fields.reduce((acc: { [key: string]: string | string[] }, field: string) => ({
                    ...acc,
                    [field]: field === 'milestones' ? [] : ''
                }), {});
            }
            
            // Initialize other sections
            filteredSections.forEach(section => {
                initialSelectedSections[section.id] = false;
                initialSectionContent[section.id] = {};
            });
            
            setSelectedSections(initialSelectedSections);
            setSectionContent(initialSectionContent);
        } catch (error) {
            console.error('Error loading sections:', error);
            setError('Erro ao carregar seções disponíveis. Por favor, tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleSectionToggle = (sectionId: string) => {
        setSelectedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));

        // Initialize required fields when section is selected
        if (!selectedSections[sectionId]) {
            const section = sections.find(s => s.id === sectionId);
            if (section) {
                setSectionContent(prev => ({
                    ...prev,
                    [sectionId]: section.required_fields.reduce((acc, field) => ({
                        ...acc,
                        [field]: field === 'milestones'
                            ? [] 
                            : field === 'diagram_url' 
                            ? '' 
                            : field === 'deliverables' && sectionId === 'timeline'
                            ? Array(project.simulation?.duration_months || 0).fill('')
                            : ''
                    }), {})
                }));
            }
        }
    };

    const handleContentChange = (sectionId: string, field: string, value: string, monthIndex?: number) => {
        setSectionContent(prev => {
            const currentContent = { ...prev[sectionId] };
            
            if (field === 'milestones') {
                // For milestones, store the raw text value
                currentContent[field] = value;
            } else if (field === 'deliverables' && typeof monthIndex === 'number') {
                // Handle deliverables for specific month
                const deliverables = [...(currentContent[field] as string[] || Array(project.simulation?.duration_months || 0).fill(''))];
                deliverables[monthIndex] = value;
                currentContent[field] = deliverables;
            } else {
                currentContent[field] = value;
            }

            return {
                ...prev,
                [sectionId]: currentContent
            };
        });
    };

    const handleFileUpload = async (sectionId: string, field: string, file: File) => {
        try {
            setUploadProgress(prev => ({ ...prev, [`${sectionId}-${field}`]: 0 }));
            
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await projectService.uploadFile(formData, (progress: number) => {
                setUploadProgress(prev => ({ 
                    ...prev, 
                    [`${sectionId}-${field}`]: progress 
                }));
            }) as UploadResponse;

            handleContentChange(sectionId, field, response.fileUrl);
            setUploadProgress(prev => ({ ...prev, [`${sectionId}-${field}`]: 100 }));
        } catch (error) {
            console.error('Error uploading file:', error);
            setError('Erro ao fazer upload do arquivo. Por favor, tente novamente.');
            setUploadProgress(prev => ({ ...prev, [`${sectionId}-${field}`]: 0 }));
        }
    };

    const handleShare = async () => {
        if (!project.simulation) {
            setError('Nenhuma simulação selecionada para o projeto.');
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            // Filter out sections that are not selected, but always include Qaracter
            const enabledSections = {
                qaracter: true,
                ...Object.keys(selectedSections)
                    .filter(key => key !== 'qaracter' && selectedSections[key])
                    .reduce((acc, key) => ({
                        ...acc,
                        [key]: selectedSections[key]
                    }), {})
            };

            // Include content for all enabled sections including Qaracter
            const enabledContent = Object.keys(enabledSections)
                .reduce((acc, key) => ({
                    ...acc,
                    [key]: sectionContent[key]
                }), {});

            const response = await projectService.createSharedProposal(project.id, {
                sections_config: enabledSections,
                sections_content: enabledContent,
                expires_in_days: 30
            });
            
            const shareUrl = `${window.location.origin}/proposals/${response.share_token}`;
            setShareUrl(shareUrl);
        } catch (error) {
            console.error('Error sharing proposal:', error);
            setError('Erro ao criar link compartilhável. Por favor, tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async () => {
        if (shareUrl) {
            try {
                await navigator.clipboard.writeText(shareUrl);
            } catch (error) {
                console.error('Error copying to clipboard:', error);
            }
        }
    };

    const renderSectionFields = (section: Section) => {
        // Don't render team fields as we'll use simulation data
        if (section.id === 'team') return null;

        return section.required_fields.map(field => {
            if (section.id === 'timeline' && field === 'deliverables') {
                const monthCount = project.simulation?.duration_months || 0;
                const startDate = project.simulation?.start_date ? new Date(project.simulation.start_date) : new Date();
                
                return (
                    <Box key={field}>
                        <Typography variant="subtitle1" gutterBottom>
                            Entregáveis por Mês ({monthCount} meses)
                        </Typography>
                        {monthCount === 0 ? (
                            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                                Nenhuma simulação selecionada ou duração não definida no projeto
                            </Typography>
                        ) : (
                            <Box sx={{ mt: 2 }}>
                                {Array.from({ length: monthCount }).map((_, index) => {
                                    const monthDate = new Date(startDate);
                                    monthDate.setMonth(monthDate.getMonth() + index);
                                    const monthName = monthDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
                                    
                                    return (
                                        <Box 
                                            key={index}
                                            sx={{ 
                                                mb: 3,
                                                p: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                backgroundColor: 'background.paper'
                                            }}
                                        >
                                            <Typography 
                                                variant="subtitle2" 
                                                gutterBottom 
                                                sx={{ 
                                                    textTransform: 'capitalize',
                                                    fontWeight: 'bold',
                                                    color: 'primary.main'
                                                }}
                                            >
                                                {monthName}
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                maxRows={6}
                                                placeholder={`Digite os entregáveis para ${monthName}`}
                                                value={sectionContent[section.id]?.[field]?.[index] || ''}
                                                onChange={(e) => handleContentChange(section.id, field, e.target.value, index)}
                                                disabled={loading}
                                                variant="outlined"
                                                size="small"
                                                helperText="Pressione Enter para adicionar novas linhas"
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                );
            }

            // Handle file upload fields
            if (field === 'diagram_url') {
                return (
                    <Box key={field} mb={2}>
                        <Box>
                            <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                id={`upload-${section.id}-${field}`}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                        handleFileUpload(section.id, field, file);
                                    }
                                }}
                                disabled={loading}
                            />
                            <label htmlFor={`upload-${section.id}-${field}`}>
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<UploadIcon />}
                                    disabled={loading}
                                >
                                    Upload Diagrama
                                </Button>
                            </label>
                            
                            {uploadProgress[`${section.id}-${field}`] > 0 && uploadProgress[`${section.id}-${field}`] < 100 && (
                                <Box sx={{ width: '100%', mt: 1 }}>
                                    <LinearProgress variant="determinate" value={uploadProgress[`${section.id}-${field}`]} />
                                </Box>
                            )}
                            
                            {sectionContent[section.id]?.[field] && (
                                <Typography variant="caption" display="block" mt={1}>
                                    Arquivo carregado: {sectionContent[section.id][field]}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            }

            // Handle all other text fields
            const isMultiline = field === 'description' || field === 'conclusion' || field === 'milestones';

            return (
                <Box key={field} mb={2}>
                    <TextField
                        fullWidth
                        label={fieldLabel(field)}
                        multiline={isMultiline}
                        minRows={field === 'milestones' ? 4 : (isMultiline ? 4 : 1)}
                        maxRows={field === 'milestones' ? 10 : (isMultiline ? 8 : 1)}
                        value={sectionContent[section.id]?.[field] || ''}
                        onChange={(e) => handleContentChange(section.id, field, e.target.value)}
                        disabled={loading}
                        helperText={field === 'milestones' ? 'Digite um milestone por linha' : (isMultiline ? 'Pressione Enter para adicionar novas linhas' : undefined)}
                        required={!(section.id === 'architecture' && field === 'diagram_url')}
                    />
                </Box>
            );
        });
    };

    return (
        <Dialog 
            open={open} 
            onClose={loading ? undefined : onClose} 
            maxWidth="md" 
            fullWidth
        >
            <DialogTitle>
                Compartilhar Proposta
                {loading && (
                    <CircularProgress
                        size={24}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: '50%',
                            transform: 'translateY(-50%)'
                        }}
                    />
                )}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {shareUrl ? (
                    <Box>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Link criado com sucesso!
                        </Alert>
                        <Box display="flex" gap={1} alignItems="center">
                            <TextField
                                fullWidth
                                label="Link da proposta"
                                value={shareUrl}
                                InputProps={{
                                    readOnly: true,
                                }}
                            />
                            <Tooltip title="Copiar link">
                                <IconButton onClick={copyToClipboard}>
                                    <ContentCopyIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                ) : (
                    <>
                        <Typography gutterBottom>
                            Selecione as seções que deseja incluir na proposta:
                        </Typography>
                        
                        {sections.map((section) => (
                            <Box key={section.id} mb={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedSections[section.id] || false}
                                            onChange={() => handleSectionToggle(section.id)}
                                            disabled={loading}
                                        />
                                    }
                                    label={
                                        <Box display="flex" alignItems="center">
                                            {section.title}
                                            <Tooltip title={`Campos obrigatórios: ${section.required_fields.map(fieldLabel).join(', ')}`}>
                                                <IconButton size="small">
                                                    <InfoIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                                
                                {selectedSections[section.id] && (
                                    <Box ml={4} mt={1}>
                                        {renderSectionFields(section)}
                                    </Box>
                                )}
                                
                                <Divider sx={{ my: 2 }} />
                            </Box>
                        ))}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {shareUrl ? 'Fechar' : 'Cancelar'}
                </Button>
                {!shareUrl && (
                    <Button 
                        onClick={handleShare} 
                        variant="contained" 
                        disabled={loading || !Object.values(selectedSections).some(selected => selected)}
                    >
                        Gerar Link
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ShareProposalDialog; 